// S8 — playback sessions, timelines, recording gaps + compliance.
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

type Ts = { utc: string; local: string; timezone: string };

// ---------------------------------------------------------------------------
// Playback sessions
// ---------------------------------------------------------------------------
export interface PlaybackStream {
  camera_id: string;
  camera_name: string;
  entity_name: string | null;
  presigned_url: string | null;
  segment_start: string | null;
  segment_end: string | null;
  has_recording: boolean;
}

export interface PlaybackSession {
  session_id: string;
  playback_start_ts: string;
  streams: PlaybackStream[];
}

export function useCreatePlaybackSession() {
  return useMutation({
    mutationFn: async (input: { camera_ids: string[]; start_ts: string }) => {
      const { data } = await api.post<PlaybackSession>(
        "/api/v1/playback/sessions",
        input,
      );
      return data;
    },
  });
}

export async function seekPlaybackSession(
  sessionId: string,
  ts: string,
): Promise<PlaybackSession> {
  const { data } = await api.get<PlaybackSession>(
    `/api/v1/playback/sessions/${sessionId}/seek`,
    { params: { ts } },
  );
  return data;
}


// ---------------------------------------------------------------------------
// Timelines
// ---------------------------------------------------------------------------
export interface TimelineSegment {
  start: Ts | null;
  end: Ts | null;
  duration_seconds: number;
}

export interface TimelineGap {
  start: Ts | null;
  end: Ts | null;
  gap_seconds: number;
}

export interface CameraTimeline {
  camera_id: string;
  segments: TimelineSegment[];
  gaps: TimelineGap[];
  compliance_pct: number | null;
}

export function useCameraTimeline(
  cameraId: string | null,
  start: string | null,
  end: string | null,
) {
  return useQuery({
    queryKey: ["playback", "timeline", cameraId, start, end],
    queryFn: async () => {
      const { data } = await api.get<CameraTimeline>(
        `/api/v1/playback/timeline/${cameraId}`,
        { params: { start_dt: start, end_dt: end } },
      );
      return data;
    },
    enabled: Boolean(cameraId && start && end),
  });
}


// ---------------------------------------------------------------------------
// Recording gaps
// ---------------------------------------------------------------------------
export interface RecordingGap {
  id: string;
  camera_id: string;
  camera_name: string | null;
  entity_name: string | null;
  gap_seconds: number;
  acknowledged: boolean;
  acknowledged_by: string | null;
  gap_start: Ts | null;
  gap_end: Ts | null;
  detected_at: Ts | null;
  acknowledged_at: Ts | null;
  ack_note: string | null;
}

export function useRecordingGaps(params: {
  cameraId?: string;
  startDate?: string;
  endDate?: string;
  acknowledged?: boolean;
}) {
  return useQuery({
    queryKey: ["recordings", "gaps", params],
    queryFn: async () => {
      const qp: Record<string, string> = {};
      if (params.cameraId) qp.camera_id = params.cameraId;
      if (params.startDate) qp.start_date = params.startDate;
      if (params.endDate) qp.end_date = params.endDate;
      if (params.acknowledged !== undefined)
        qp.acknowledged = String(params.acknowledged);
      const { data } = await api.get<RecordingGap[]>(
        "/api/v1/recordings/gaps",
        { params: qp },
      );
      return data;
    },
  });
}

export function useAcknowledgeGap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { gapId: string; note?: string }) => {
      const { data } = await api.post<RecordingGap>(
        `/api/v1/recordings/gaps/${input.gapId}/acknowledge`,
        { note: input.note },
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recordings", "gaps"] });
    },
  });
}


// ---------------------------------------------------------------------------
// Compliance dashboard
// ---------------------------------------------------------------------------
export interface ComplianceItem {
  camera_id: string;
  camera_name: string | null;
  entity_name: string | null;
  date: string;
  expected_seconds: number;
  actual_seconds: number;
  gap_count: number;
  compliance_pct: number;
}

export function useCompliance(date: string | null) {
  return useQuery({
    queryKey: ["recordings", "compliance", date],
    queryFn: async () => {
      const { data } = await api.get<{ items: ComplianceItem[]; total: number }>(
        "/api/v1/recordings/compliance",
        { params: { date } },
      );
      return data;
    },
    enabled: Boolean(date),
  });
}

export function useRecalculateCompliance() {
  return useMutation({
    mutationFn: async (input: { camera_id?: string; date: string }) => {
      const { data } = await api.post<{ task_id: string; message: string }>(
        "/api/v1/recordings/compliance/recalculate",
        input,
      );
      return data;
    },
  });
}


// ---------------------------------------------------------------------------
// Recording config (S8 PUT shape)
// ---------------------------------------------------------------------------
export interface ScheduleWindow {
  day: number; // 0..6 (Mon..Sun)
  start: string; // HH:MM
  end: string;   // HH:MM
}

export interface RecordingConfigS8 {
  id: string;
  camera_id: string;
  enabled: boolean;
  mode: "continuous" | "schedule" | "motion";
  retention_minutes: number;
  schedule_enabled: boolean;
  schedule_windows: ScheduleWindow[];
  motion_triggered: boolean;
  min_clip_duration_seconds: number;
  pre_event_seconds: number;
  post_event_seconds: number;
  created_at: string;
}

export function useRecordingConfigS8(cameraId: string | null) {
  return useQuery({
    queryKey: ["recordings", "config-s8", cameraId],
    queryFn: async () => {
      const { data } = await api.get<RecordingConfigS8 | null>(
        `/api/v1/recordings/config/${cameraId}`,
      );
      return data;
    },
    enabled: Boolean(cameraId),
  });
}

export function usePutRecordingConfigS8(cameraId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<RecordingConfigS8, "id" | "camera_id" | "created_at">>) => {
      const { data } = await api.put<RecordingConfigS8>(
        `/api/v1/recordings/config/${cameraId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recordings", "config-s8"] });
    },
  });
}
